import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { DisclaimerBanner } from "@/components/DisclaimerBanner";
import { useLang } from "@/contexts/LanguageContext";
import { StatusPill } from "@/components/StatusPill";
import { isSanctionedOutcome, isClosedOutcome, AWAITING_DISBURSEMENT } from "@/lib/statuses";
import { useAuth } from "@/contexts/AuthContext";
import { PackageSearch, ClipboardList, ListChecks, BarChart3, UserCheck, Clock, FileWarning, Calendar, Send, Building2, MessageSquare } from "lucide-react";

function StatCard({ icon: Icon, label, value, sub, to, testId, color = "slate" }) {
  return (
    <Link to={to} data-testid={testId} className="p-6 rounded-lg border border-slate-200 bg-white card-hover flex items-start gap-4">
      <div className={`w-10 h-10 rounded-md grid place-items-center text-white shrink-0 ${color === "orange" ? "bg-orange-500" : color === "emerald" ? "bg-emerald-500" : "bg-slate-900"}`}><Icon className="w-5 h-5" /></div>
      <div className="min-w-0 flex-1">
        <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
        <div className="mt-1 text-2xl font-bold text-slate-900 brand-heading">{value}</div>
        {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
      </div>
    </Link>
  );
}

// Buckets use the raw lender-side statuses recorded by the bank (lower-cased).
const RM_QUEUE_ORDER = [
  { key: "new_assigned",       label: "New assigned opportunities", icon: PackageSearch, statuses: null },
  { key: "awaiting_contact",   label: "Awaiting contact",           icon: Clock,          statuses: ["application shared"] },
  { key: "contact_attempts",   label: "Interest sent, awaiting MSME", icon: MessageSquare, statuses: null },
  { key: "docs_pending",       label: "Documents pending",          icon: FileWarning,    statuses: ["additional documents required", "documentation pending"] },
  { key: "meetings",           label: "Unit visits scheduled",      icon: Calendar,       statuses: ["field verification"] },
  { key: "initial_assessment", label: "Initial assessment",         icon: UserCheck,      statuses: ["initial review"] },
  { key: "branch_referral",    label: "Branch referral",            icon: Building2,      statuses: ["assigned to branch"] },
  { key: "app_initiated",      label: "Under appraisal",            icon: Send,           statuses: ["credit appraisal", "credit decision pending", "indicatively approved"] },
];

function RMWorkspace({ opps, apps }) {
  const { t, tv } = useLang();
  const byStatus = apps.reduce((acc, a) => {
    const k = String(a.status || "").toLowerCase();
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});
  const bucketCount = (s) => s.reduce((sum, k) => sum + (byStatus[k] || 0), 0);
  const tileCount = (q) => {
    if (q.key === "new_assigned") return opps.filter((o) => !o.expressed_interest).length;
    if (q.key === "contact_attempts") return Math.max(0, opps.filter((o) => o.expressed_interest).length - apps.length);
    return bucketCount(q.statuses);
  };
  const sanctioned = apps.filter((a) => isSanctionedOutcome(a.status)).length;
  const declined = apps.filter((a) => isClosedOutcome(a.status)).length;
  return (
    <div className="space-y-8" data-testid="rm-workspace">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 brand-heading">{t("rm_title")}</h1>
        <p className="text-slate-600 mt-1 text-sm">{t("rm_sub")}</p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="rm-queue-tiles">
        {RM_QUEUE_ORDER.map((q) => {
          const Icon = q.icon;
          const n = tileCount(q);
          return (
            <Link key={q.key} to="/bank/applications" data-testid={`rm-tile-${q.key}`} className="p-5 rounded-lg border border-slate-200 bg-white card-hover">
              <div className="flex items-center gap-2 text-slate-500"><Icon className="w-4 h-4" /><div className="text-[10px] uppercase tracking-widest">{t("queue")}</div></div>
              <div className="mt-2 text-2xl font-bold text-slate-900 brand-heading">{n}</div>
              <div className="text-xs text-slate-600 mt-0.5">{t(`rm_q_${q.key}`)}</div>
            </Link>
          );
        })}
      </div>
      <div className="grid lg:grid-cols-3 gap-3 sm:gap-4">
        <StatCard icon={ClipboardList} label={t("rm_outcome")} value={`${sanctioned}`} sub={`${declined} ${t("rm_declined_sub")}`} to="/bank/applications" testId="rm-outcome-sanction" color="emerald" />
        <StatCard icon={PackageSearch} label={t("rm_flight")} value={apps.length - sanctioned - declined} sub={t("rm_followup")} to="/bank/applications" testId="rm-outcome-flight" color="orange" />
        <StatCard icon={BarChart3} label={t("rm_disb")} value={apps.filter((a)=>AWAITING_DISBURSEMENT.includes(String(a.status||"").toLowerCase())).length} sub={t("rm_disb_sub")} to="/bank/applications" testId="rm-outcome-disburse" />
      </div>
      <div className="p-6 rounded-lg border border-slate-200 bg-white">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-slate-900 brand-heading">{t("rm_history")}</h2>
          <Link to="/bank/applications" className="text-xs text-slate-900 font-semibold hover:text-orange-600">{t("rm_open_queue")}</Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-widest text-slate-500 border-b border-slate-200">
              <tr><th className="py-2 pr-4">{t("col_ref_short")}</th><th className="py-2 pr-4">{t("col_borrower")}</th><th className="py-2 pr-4">{t("col_amount")}</th><th className="py-2 pr-4">{t("col_ref")}</th><th className="py-2 pr-4">{t("col_status")}</th></tr>
            </thead>
            <tbody>
              {apps.slice(0, 10).map((a) => (
                <tr key={a.id} className="border-b border-slate-100" data-testid={`rm-followup-${a.id}`}>
                  <td className="py-3 pr-4 font-mono text-xs">{(a.id || "").slice(0,8)}</td>
                  <td className="py-3 pr-4">{a.business_name || "—"}</td>
                  <td className="py-3 pr-4 font-semibold">{formatINR(a.requested_amount || a.amount || 0)}</td>
                  <td className="py-3 pr-4 font-mono text-xs">{a.lender_ref_no || "—"}</td>
                  <td className="py-3 pr-4"><StatusPill status={a.status} /></td>
                </tr>
              ))}
              {apps.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-slate-500">{t("rm_none")}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <DisclaimerBanner compact />
    </div>
  );
}

export default function BankDashboard() {
  const { user } = useAuth();
  const [opps, setOpps] = useState([]);
  const [apps, setApps] = useState([]);
  const [policyCount, setPolicyCount] = useState(null);
  const { t, tv } = useLang();
  useEffect(() => {
    api.get("/lender/opportunities").then((r) => setOpps(r.data)).catch(() => {});
    api.get("/applications").then((r) => setApps(r.data)).catch(() => {});
    if (user?.role === "bank_admin") api.get("/lender-policies").then((r) => setPolicyCount(r.data.length)).catch(() => {});
  }, [user?.role]);
  // Relationship managers get the operational queue view; bank admins keep the analytical overview.
  if (user?.role === "bank_rm") return <RMWorkspace opps={opps} apps={apps} />;
  const strong = opps.filter((o) => o.category === "Strong Match").length;
  const sanctioned = apps.filter((a) => isSanctionedOutcome(a.status)).length;
  const conversion = apps.length ? Math.round((sanctioned / apps.length) * 100) : 0;
  return (
    <div className="space-y-8" data-testid="bank-dashboard">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 brand-heading">{t("bd_title")}</h1>
        <p className="text-slate-600 mt-1 text-sm">{t("bd_sub")}</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard icon={PackageSearch} label={t("bd_new")} value={opps.length} sub={`${strong} ${t("bd_strong")}`} to="/bank/opportunities" testId="bd-opps" color="orange" />
        <StatCard icon={ClipboardList} label={t("applications")} value={apps.length} sub={`${sanctioned} ${t("bd_sanctioned")}`} to="/bank/applications" testId="bd-apps" color="emerald" />
        <StatCard icon={BarChart3} label={t("bd_conv")} value={`${conversion}%`} sub={t("bd_conv_sub")} to="/bank/analytics" testId="bd-conv" />
        <StatCard icon={ListChecks} label={t("policies")} value={policyCount ?? "—"} sub={t("bd_products")} to="/bank/policies" testId="bd-policies" />
      </div>
      <div className="p-6 rounded-lg border border-slate-200 bg-white">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-slate-900 brand-heading">{t("bd_top")}</h2>
          <Link to="/bank/opportunities" className="text-xs text-slate-900 font-semibold hover:text-orange-600">{t("bd_market")}</Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-widest text-slate-500 border-b border-slate-200">
              <tr><th className="py-2 pr-4">{t("col_district")}</th><th className="py-2 pr-4">{t("col_sector")}</th><th className="py-2 pr-4">{t("col_requested")}</th><th className="py-2 pr-4">{t("col_readiness")}</th><th className="py-2 pr-4">{t("col_match")}</th><th className="py-2 pr-4">{t("col_category")}</th></tr>
            </thead>
            <tbody>
              {opps.slice(0, 8).map((o) => (
                <tr key={o.id} className="border-b border-slate-100">
                  <td className="py-3 pr-4">{o.district}</td><td className="py-3 pr-4">{tv(o.sector)}</td><td className="py-3 pr-4 font-semibold">{formatINR(o.requested_amount)}</td><td className="py-3 pr-4">{o.readiness_band ? tv(o.readiness_band) : "—"}</td>
                  <td className="py-3 pr-4 font-bold text-slate-900">{o.match_percentage}%</td>
                  <td className="py-3 pr-4"><span className="rounded-full border border-slate-200 bg-slate-50 text-xs px-2 py-0.5">{tv(o.category)}</span></td>
                </tr>
              ))}
              {opps.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-slate-500">{t("bd_none")}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <DisclaimerBanner compact />
    </div>
  );
}
